import { createConsumer } from '@rails/actioncable';

const PRESENCE_INTERVAL = 20000;
const RECONNECT_INTERVAL = 1000;

class BaseActionCableConnector {
  // Era `static`. Um conector orfao (a troca de conta cria um novo sem
  // desligar o anterior) escrevia neste flag e o conector VIVO lia o valor
  // do orfao -- estado mutavel compartilhado entre instancias que nao se
  // conhecem. Por instancia, cada conector so responde pela propria conexao.
  isDisconnected = false;

  // Marca que este conector esta sendo substituido de proposito, para o
  // callback `disconnected` nao confundir teardown com queda de rede.
  isTearingDown = false;

  constructor(
    app,
    pubsubToken,
    websocketHost = '',
    presenceInterval = PRESENCE_INTERVAL
  ) {
    const websocketURL = websocketHost ? `${websocketHost}/cable` : undefined;

    this.consumer = createConsumer(websocketURL);
    this.subscription = this.consumer.subscriptions.create(
      {
        channel: 'RoomChannel',
        pubsub_token: pubsubToken,
        account_id: app.$store.getters.getCurrentAccountId,
        user_id: app.$store.getters.getCurrentUserID,
      },
      {
        updatePresence() {
          this.perform('update_presence');
        },
        received: this.onReceived,
        disconnected: () => {
          // Desligamento deliberado (troca de conta -> este conector foi
          // substituido) NAO e queda de rede. Sem esta guarda, o proprio
          // teardown emitia WEBSOCKET_DISCONNECT e a UI acendia o aviso de
          // "Desconectado" 3s depois -- com a conexao NOVA aberta e saudavel,
          // e sem nunca apagar, porque o conector novo nao caiu e portanto
          // nada agenda o checkConnection que emitiria a reconexao.
          if (this.isTearingDown) return;
          this.isDisconnected = true;
          this.onDisconnected();
          this.initReconnectTimer();
        },
      }
    );
    this.app = app;
    this.events = {};
    this.reconnectTimer = null;
    this.isAValidEvent = () => true;
    this.presenceTimer = null;
    this.triggerPresenceInterval = () => {
      this.presenceTimer = setTimeout(() => {
        this.subscription.updatePresence();
        this.triggerPresenceInterval();
      }, presenceInterval);
    };
    this.triggerPresenceInterval();
  }

  checkConnection() {
    const isConnectionActive = this.consumer.connection.isOpen();
    const isReconnected = this.isDisconnected && isConnectionActive;
    if (isReconnected) {
      this.clearReconnectTimer();
      this.onReconnect();
      this.isDisconnected = false;
    } else {
      this.initReconnectTimer();
    }
  }

  clearReconnectTimer = () => {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  };

  initReconnectTimer = () => {
    this.clearReconnectTimer();
    this.reconnectTimer = setTimeout(() => {
      this.checkConnection();
    }, RECONNECT_INTERVAL);
  };

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {};

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {};

  // Desliga tudo que esta instancia mantem vivo: o timer de presenca (que
  // continuava batendo de minuto em minuto num conector abandonado), o timer
  // de reconexao e a propria conexao.
  disconnect() {
    this.isTearingDown = true;
    this.clearReconnectTimer();
    if (this.presenceTimer) {
      clearTimeout(this.presenceTimer);
      this.presenceTimer = null;
    }
    this.consumer.disconnect();
  }

  onReceived = ({ event, data } = {}) => {
    if (this.isAValidEvent(data)) {
      if (this.events[event] && typeof this.events[event] === 'function') {
        this.events[event](data);
      }
    }
  };
}

export default BaseActionCableConnector;
